/* eslint-disable no-restricted-globals */
import parseMax from 'libphonenumber-js/max';

import { isArray, isEmpty, trim } from 'lodash';
import equalsValidator from 'validator/lib/equals';
import isEmailValidator from 'validator/lib/isEmail';
import isLengthValidator from 'validator/lib/isLength';
import isUrlValidator from 'validator/lib/isURL';
import isAlpha from 'validator/lib/isAlpha';
import isUUID from 'validator/lib/isUUID';
import { isValidIBAN } from 'ibantools';
import checkVAT from 'utils/vat/checkVAT';

import { __t, applyTranslations } from './language';
import {
  isStrictInstagramPostUrl,
  isStrictInstagramReelUrl,
  isTikTokVideoUrl,
  isYoutubeUrl,
  isZalandoFashionStoreUrl
} from './url';
import { isEmptyEditorText } from './editor';
import { WMC_MARKETS } from './wmc';

applyTranslations(require('./i18n/validators.i18n.json'));

export const NAME_MAX_LENGTH = 255;
export const SHORT_STRING_MAX_LENGTH = 100;
export const LONG_STRING_MAX_LENGTH = 30000;

export const GENERIC_REQUIRED_FIELD_MESSAGE = __t('validators.required_field');

function getGenericInvalidURLMessage(format) {
  return format
    ? __t('validators.invalid_url_format', { format })
    : __t('validators.invalid_url');
}

function getStringValue(value) {
  return value || ''; // sometimes values is null, while validatorjs supports only strings
}

export function isRequired(value, message = GENERIC_REQUIRED_FIELD_MESSAGE) {
  return {
    valid: !!trim(getStringValue(value)),
    message
  };
}

export function isYoutube(value) {
  return {
    valid: isYoutubeUrl(value),
    message: __t('validators.invalid_youtube_url')
  };
}

export function isPhoneNumber(value, message) {
  const phoneNumber = parseMax(value);
  return {
    valid: !!phoneNumber?.isValid(),
    message
  };
}

// Draft-js will insert special characters like \u200B just by adding focus to the editor. This
// allows the wysiwyg to be submitted without actually entering text. This method prevents that.
export function isRequiredRichText(value) {
  return {
    valid: !isEmptyEditorText(value),
    message: GENERIC_REQUIRED_FIELD_MESSAGE
  };
}

export function isArrayRequired(value, message = GENERIC_REQUIRED_FIELD_MESSAGE) {
  return {
    valid: isArray(value) && value.length > 0,
    message
  };
}

export function isMultipleItemsInArrayRequired(value, message = GENERIC_REQUIRED_FIELD_MESSAGE, reqNumberOfArray = 1) {
  return {
    valid: isArray(value) && value.length > reqNumberOfArray,
    message
  };
}

export function isBooleanRequired(value) {
  return {
    valid: value !== undefined && value !== null,
    message: GENERIC_REQUIRED_FIELD_MESSAGE
  };
}

export function maxLength(value, length) {
  return {
    valid: isLengthValidator(getStringValue(value), { max: length }),
    message: __t('validators.validation_max_length_exceeded', { maxLength: length })
  };
}

export function minLength(value, length) {
  return {
    valid: isLengthValidator(getStringValue(value), { min: length }),
    message: __t('validators.validation_min_length_exceeded', { minLength: length })
  };
}

export function maxLengthArray(
  arr,
  length,
  message = __t('validators.validation_max_length_array_exceeded', { maxLength: length })) {
  return {
    valid: arr && arr.length <= length,
    message
  };
}

export function minLengthArray(arr, length, verbOverride) {
  const noun = __t(`validators.label_${length !== 1
    ? __t('validators.label_items')
    : __t('validators.label_item')}`
  );

  return {
    valid: arr && arr.length >= length,
    message: __t('validators.validation_min_length_array', {
      minLength: length,
      verb: verbOverride || __t('validators.label_selected'),
      noun
    })
  };
}

export function isEmail(value) {
  return {
    valid: isEmailValidator(getStringValue(value)),
    message: __t('validators.invalid_email')
  };
}

export function isArrayOfEmail(value) {
  return {
    valid: value.every((email) => isEmailValidator(getStringValue(email))),
    message: __t('validators.invalid_emails')
  };
}

export function isUrl(value, format = 'https://example.com') {
  const stringValue = getStringValue(value);
  const urlOptions = {
    protocols: ['http', 'https'],
    require_valid_protocol: false
  };

  return {
    valid: !stringValue || isUrlValidator(stringValue, urlOptions),
    message: getGenericInvalidURLMessage(format)
  };
}

export function isNotEmpty(value, message) {
  return {
    valid: !!value,
    message: message || GENERIC_REQUIRED_FIELD_MESSAGE
  };
}

export function isValidPassword(value) {
  const stringValue = getStringValue(value);
  return {
    valid: /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[\W_]).{8,}$/.test(stringValue),
    message: __t('validators.password_should_be_strong')
  };
}

export function isPositiveNumber(value) {
  const DEFAULT_VALUE = 0;
  const currentValue = value || DEFAULT_VALUE; // value could be null or empty string
  const number = Number(currentValue);

  return {
    valid: !isNaN(number) && isFinite(number) && number >= 0,
    message: __t('validators.invalid_numeric_field')
  };
}

export function isGreaterThanZero(value) {
  return {
    valid: value > 0,
    message: __t('validators.invalid_greater_than_zero')
  };
}

export function isLesserThanOrEqualTo(value1, value2, message) {
  return {
    valid: (!value1 || !value2) ? true : (value1 <= value2),
    message
  };
}

export function isGreaterThanOrEqualTo(value1, value2, message) {
  return {
    valid: (!value1 || !value2) ? true : (value1 >= value2),
    message
  };
}

export function isGreaterThanMaxAllowedChars(value, maxChars) {
  return {
    valid: value.length <= maxChars,
    message: __t('validators.invalid_greater_than_max_allowed_chars')
  };
}

export function isOneOf(value, allowedValues = [], message = '') {
  return {
    valid: allowedValues.indexOf(value) !== -1,
    message
  };
}

export function equals(value, other, message) {
  return {
    valid: equalsValidator(value, other),
    message
  };
}

export function isFalsy(value, message) {
  return {
    valid: !value,
    message
  };
}

export function isInstagramPostUrl(value, format = 'https://www.instagram.com/p/yourpost/') {
  const stringValue = trim(getStringValue(value));
  const urlOptions = {
    protocols: ['http', 'https'],
    require_valid_protocol: false
  };

  return {
    valid: !stringValue || (isUrlValidator(stringValue, urlOptions) && isStrictInstagramPostUrl(stringValue)),
    message: getGenericInvalidURLMessage(format)
  };
}

export function isInstagramReelUrl(value, format = 'https://www.instagram.com/reel/yourreel/') {
  const stringValue = trim(getStringValue(value));
  const urlOptions = {
    protocols: ['http', 'https'],
    require_valid_protocol: false
  };

  return {
    valid: !stringValue
      || (isUrlValidator(stringValue, urlOptions) && (
        // ig reel can be accessed as both post and reel
        isStrictInstagramReelUrl(stringValue) || isStrictInstagramPostUrl(stringValue)
      )),
    message: getGenericInvalidURLMessage(format)
  };
}

export function isValidTikTokVideoUrl(value, format = 'https://www.tiktok.com/@user/video/1234567890') {
  const stringValue = trim(getStringValue(value));
  const urlOptions = {
    protocols: ['http', 'https'],
    require_valid_protocol: false
  };

  return {
    valid: !stringValue || (isUrlValidator(stringValue, urlOptions) && isTikTokVideoUrl(stringValue)),
    message: getGenericInvalidURLMessage(format)
  };
}

export function isYoutubeVideoUrl(value, format = 'https://www.youtube.com/watch?v=yourvideo') {
  const stringValue = trim(getStringValue(value));
  const urlOptions = {
    protocols: ['http', 'https'],
    require_valid_protocol: false
  };

  return {
    valid: !stringValue || (isUrlValidator(stringValue, urlOptions) && isYoutubeUrl(stringValue)),
    message: getGenericInvalidURLMessage(format)
  };
}

export function isValidDateRange(startDate, endDate, message) {
  return {
    valid: (endDate && endDate.localeCompare(startDate) > -1) || false,
    message: message || __t('validators.invalid_date_range')
  };
}

export function isValidChannelUrl(channelType, url) {
  switch (channelType) {
    case 'instagram':
      return /^https:\/\/(www\.)?instagram\.com\/.+/.test(url)
        && !/^https:\/\/(www\.)?instagram\.com\/p\/.+/.test(url);
    case 'youtube':
      return /^((https?):\/\/)?(www.)?youtube\.com\/(@?[a-zA-Z0-9])/.test(url);
    case 'blog':
      return true;
    case 'twitter':
      return /^https:\/\/(www\.)?twitter\.com\/.+/.test(url);
    case 'snapchat':
      return /^https:\/\/(www\.)?snapchat\.com\/add\/.+/.test(url);
    case 'facebook':
      return /^https:\/\/(www\.)?facebook\.com\/.+/.test(url);
    case 'pinterest':
      return /^https:\/\/(www\.)?pinterest\.com\/.+/.test(url);
    case 'tumblr':
      return /^http(s)?:\/\/(www\.)?[^.]+\.tumblr\.com/.test(url);
    case 'tiktok':
      return /^http(s)?:\/\/(www\.)?tiktok\.com\/@/.test(url);
    default:
      return false;
  }
}

export function isChannelUrl(channelType, value, format) {
  const stringValue = getStringValue(value);

  if (!stringValue) {
    return {
      valid: true,
      message: ''
    };
  }

  const urlOptions = {
    protocols: ['http', 'https'],
    require_valid_protocol: true
  };

  const isValidUrl = isUrlValidator(stringValue, urlOptions);
  const isValidChannel = isValidChannelUrl(channelType, value);

  return {
    valid: isValidUrl && isValidChannel,
    message: getGenericInvalidURLMessage(format)
  };
}

export function isValidAccountNumber(account) {
  const message = __t('validators.bank_details_account_number_error');
  // We have to clean up string otherwise lib would give incorrect result.
  const accountNumber = account
    .replace(/\s+/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase()
    .trim();

  return {
    // only validate if the two first digits are letters => EU iban
    valid: isAlpha(accountNumber.substring(0, 2)) ? isValidIBAN(accountNumber) : true,
    message
  };
}

export function isValidVATNumber(vat) {
  const message = __t('validators.address_details_tax_id_error');

  return {
    // only validate if the two first digits are letters => EU VAT Number
    valid: isAlpha(vat.substring(0, 2)) ? Boolean(checkVAT(vat).country) : true,
    message
  };
}

export function isMoneySum(value, message = __t('validators.invalid_money_sum')) {
  const formattedString = (value ?? '').toString().replace(/,/g, ''); // strip all separators
  const isValid = formattedString === '' || /^[0-9]+(\.[0-9]{0,2})?$/.test(formattedString);

  return {
    valid: isValid,
    message
  };
}

export function isIntegerMoneySum(value, message = __t('validators.invalid_integer_money_sum')) {
  const formattedString = (value ?? '').toString();
  const isValid = formattedString === '' || /^[0-9]+$/.test(formattedString);

  return {
    valid: isValid,
    message
  };
}

export function isZalandoPage(url = '', message = __t('validators.invalid_zalando_page')) {
  const hasProtocol = /(http(s?)):\/\//i.test(url);
  let domain = '';

  const updatedUrl = !isEmpty(url) && !hasProtocol
    ? `https://${url}`
    : url;

  try {
    const urlParts = new URL(updatedUrl, 'https://collabary.com');
    domain = urlParts.host.replace(/[www|m]+\D/, ''); // strip www. | m. | m-
  } catch (error) {} // eslint-disable-line no-empty

  return {
    valid: isEmpty(updatedUrl) || WMC_MARKETS.includes(domain),
    message
  };
}

export function isZalandoCollectionLink(
  value,
  format = 'https://en.zalando.com/collections/mycollectionid/ or https://en.zalando.com/catalogue/brandname/'
) {
  const stringValue = trim(getStringValue(value));
  const urlOptions = {
    protocols: ['http', 'https'],
    require_valid_protocol: false
  };

  return {
    valid: !stringValue
      || (isUrlValidator(stringValue, urlOptions) && isZalandoFashionStoreUrl(stringValue)),
    message: getGenericInvalidURLMessage(format)
  };
}

export function isNcode(value = '', message = __t('validators.invalid_ncode')) {
  const formattedString = String(value);
  const isValid = formattedString === '' || /^N\d{7}$/gm.test(formattedString);

  return {
    valid: isValid,
    message
  };
}

export function isUuid(value = '', message = __t('validators.invalid_uuid')) {
  const formattedString = String(value);
  const isValid = formattedString === '' || isUUID(formattedString);

  return {
    valid: isValid,
    message
  };
}
